/**
 * Person profile — one composite call renders the whole page.
 *
 * Tabs that do not apply are HIDDEN, not disabled: no Talent tab without a talent profile. A
 * disabled tab is a promise the record does not keep.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { tfApi } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import { EditPersonModal } from '../components/ui/edit_person_modal'
import { InlineEditCell } from '../components/ui/inline_edit_cell'
import {
  Avatar,
  ConfirmDialog,
  DueBadge,
  EmptyState,
  ErrorState,
  ModalShell,
  RelationshipTriad,
  Spinner,
  StatusBadge,
  Tag,
  addDaysIso,
  formatDate,
  formatShortDate,
} from '../components/ui/primitives'
import type { PersonProfile } from '../types/tf_types'

type TabKey = 'overview' | 'activities' | 'opportunities' | 'talent' | 'files' | 'relationships'

export function PersonProfilePage() {
  const { contactId } = useParams()
  const id = Number(contactId)
  const { options, notify, notifyError } = useApp()

  const [profile, setProfile] = useState<PersonProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; technical?: string } | null>(null)
  const [tab, setTab] = useState<TabKey>('overview')
  const [logOpen, setLogOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setProfile(await tfApi.fetchPersonProfile(id))
    } catch (err: any) {
      setError({ message: err?.detail?.message ?? String(err), technical: err?.detail?.technical })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const reassign = useCallback(async (next: string | null) => {
    try {
      const result = await tfApi.setPersonOrganization(id, next)
      notify({
        tone: 'ok',
        title: result.OrganizationCreated ? 'Organization created' : 'Organization changed',
        body: result.OrganizationName
          ? result.OrganizationCreated
            ? `${result.OrganizationName} did not exist, so it was created and linked.`
            : `Moved to ${result.OrganizationName}.`
          : 'No longer linked to a company.',
      })
      await load()
    } catch (err) {
      notifyError('Could not change the organization', err)
      throw err
    }
  }, [id, load, notify, notifyError])

  const remove = useCallback(async () => {
    setRemoveOpen(false)
    try {
      const result = await tfApi.deletePerson(id)
      notify({
        tone: 'ok',
        title: `${result.DisplayName} removed`,
        body: result.CancelledFollowUps
          ? `${result.CancelledFollowUps} open follow-up${result.CancelledFollowUps === 1 ? ' was' : 's were'} cancelled. Their history is kept.`
          : 'Their activity history is kept.',
      })
      //  Back to the list: staying on a profile that no longer appears anywhere reads as though
      //  the removal did not happen.
      navigate('/home/my-relationships')
    } catch (err) {
      notifyError('Could not remove this person', err)
    }
  }, [id, navigate, notify, notifyError])

  const patch = useCallback(
    (field: string, label: string) => async (next: string | null) => {
      try {
        await tfApi.updateRelationship(id, { [field]: next } as any)
        notify({ tone: 'ok', title: 'Saved', body: `${label} updated` })
        await load()
      } catch (err) {
        notifyError(`Could not update ${label.toLowerCase()}`, err)
        throw err
      }
    },
    [id, load, notify, notifyError],
  )

  const toggleWaiting = async (waiting: boolean) => {
    try {
      await tfApi.setWaiting(id, {
        WaitingForResponse: waiting,
        FollowUpIfNoResponseBy: waiting ? addDaysIso(7) : null,
      })
      notify({
        tone: 'ok',
        title: waiting ? 'Marked as waiting' : 'No longer waiting',
        body: waiting ? 'They owe you a reply — this leaves your action list.' : 'Back in your action list.',
      })
      await load()
    } catch (err) {
      notifyError('Could not update waiting state', err)
    }
  }

  if (loading) return <><PageHeader crumb="Tidalfin / Relationships / People" title="Loading…" /><Spinner /></>
  if (error || !profile) {
    return (
      <>
        <PageHeader crumb="Tidalfin / Relationships / People" title="Person" />
        <div className="body-scroll">
          <ErrorState title="Could not load this person" message={error?.message} technical={error?.technical} onRetry={() => void load()} />
        </div>
      </>
    )
  }

  const c = profile.Contact
  const rel = profile.Relationship
  const name = c.DisplayName || [c.FirstName, c.LastName].filter(Boolean).join(' ') || `Contact ${id}`
  const hasTalent = Boolean(profile.TalentProfile)

  const tabs: Array<{ key: TabKey; label: string; icon: string; count?: number; show: boolean }> = [
    { key: 'overview', label: 'Overview', icon: 'summarize', show: true },
    { key: 'activities', label: 'Activities', icon: 'history', count: profile.Activities.length, show: true },
    { key: 'opportunities', label: 'Opportunities', icon: 'trending_up', count: profile.Opportunities.length, show: true },
    //  Hidden, not disabled.
    { key: 'talent', label: 'Talent', icon: 'badge', show: hasTalent },
    { key: 'files', label: 'Files', icon: 'folder', count: profile.Documents.length, show: true },
    { key: 'relationships', label: 'Relationships', icon: 'hub', count: profile.Links.length, show: true },
  ]

  return (
    <>
      <PageHeader
        crumb={<><Link to="/relationships/people">Tidalfin / Relationships / People</Link> / {name}</>}
        title={name}
        actions={
          <>
            <button className="btn secondary" type="button" onClick={() => setEditOpen(true)}>
              <span className="material-symbols-outlined">edit</span>Edit
            </button>
            <button className="btn secondary" type="button" onClick={() => setNoteOpen(true)}>
              <span className="material-symbols-outlined">sticky_note_2</span>Add Note
            </button>
            <button className="btn primary" type="button" onClick={() => setLogOpen(true)}>
              <span className="material-symbols-outlined">add</span>Log Activity
            </button>
            <button className="btn ghost" type="button" onClick={() => setRemoveOpen(true)}>
              <span className="material-symbols-outlined">person_remove</span>Remove
            </button>
          </>
        }
      />

      <div className="body-scroll">
        <div className="profile-head">
          <div className="ph-identity">
            <Avatar name={name} large />
            <div className="ph-main">
              <div className="ph-name">{name}</div>
              <div className="ph-role">
                {[c.JobTitle, profile.Organization?.CompanyName].filter(Boolean).join(' · ') || '—'}
              </div>
              <div className="ph-meta">
                {c.PrimaryEmail && (
                  <span className="ph-meta-item">
                    <span className="material-symbols-outlined">mail</span>
                    <a href={`mailto:${c.PrimaryEmail}`}>{c.PrimaryEmail}</a>
                  </span>
                )}
                {c.PrimaryPhone && (
                  <span className="ph-meta-item">
                    <span className="material-symbols-outlined">call</span>
                    <a href={`tel:${c.PrimaryPhone}`}>{c.PrimaryPhone}</a>
                  </span>
                )}
                {c.LinkedInPage && (
                  <span className="ph-meta-item">
                    <span className="material-symbols-outlined">link</span>
                    <a href={c.LinkedInPage} target="_blank" rel="noreferrer">LinkedIn</a>
                  </span>
                )}
                <span className="ph-meta-item">
                  <span className="material-symbols-outlined">tag</span>
                  <code style={{ fontFamily: 'var(--tf-font-mono)', fontSize: 'var(--fs-xs)' }}>
                    PERSON-{id}
                  </code>
                  <button
                    className="utility-btn"
                    type="button"
                    style={{ width: 22, height: 22 }}
                    aria-label="Copy person id"
                    onClick={() => {
                      void navigator.clipboard?.writeText(`PERSON-${id}`)
                      notify({ tone: 'info', title: 'Copied', body: `PERSON-${id}` })
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
                  </button>
                </span>
              </div>
              <div className="ph-badges">
                <StatusBadge domain="RELATIONSHIP_STRENGTH" value={rel?.RelationshipStrength} />
                {rel?.ContactStatus && <StatusBadge domain="CONTACT_STATUS" value={rel.ContactStatus} />}
                {profile.RelationshipTypes.map((t: any) => (
                  <Tag key={t.RelationshipTypeAssignmentId}>{t.RelationshipType}</Tag>
                ))}
                {profile.Tags.map((t) => <Tag key={t}>{t}</Tag>)}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <RelationshipTriad
              status={rel?.ContactStatus}
              nextMove={rel?.NextMove}
              followUpDate={rel?.NextFollowUpDate}
              waiting={Boolean(rel?.WaitingForResponse)}
            />
          </div>

          <div className="quick-actions">
            {c.PrimaryEmail && (
              <a className="btn secondary sm" href={`mailto:${c.PrimaryEmail}`}>
                <span className="material-symbols-outlined">mail</span>Email
              </a>
            )}
            {c.PrimaryPhone && (
              <a className="btn secondary sm" href={`tel:${c.PrimaryPhone}`}>
                <span className="material-symbols-outlined">call</span>Call
              </a>
            )}
            <button className="btn secondary sm" type="button" onClick={() => setLogOpen(true)}>
              <span className="material-symbols-outlined">history</span>Log Activity
            </button>
            <button
              className={`btn ${rel?.WaitingForResponse ? 'primary' : 'secondary'} sm`}
              type="button"
              onClick={() => void toggleWaiting(!rel?.WaitingForResponse)}
            >
              <span className="material-symbols-outlined">hourglass_top</span>
              {rel?.WaitingForResponse ? 'They replied' : 'Waiting on them'}
            </button>
          </div>
        </div>

        <div className="tab-strip" role="tablist">
          {tabs.filter((t) => t.show).map((t) => (
            <button
              key={t.key}
              className={`tab${tab === t.key ? ' active' : ''}`}
              role="tab"
              aria-selected={tab === t.key}
              type="button"
              onClick={() => setTab(t.key)}
            >
              <span className="material-symbols-outlined">{t.icon}</span>
              {t.label}
              {t.count !== undefined && <span className="tcount">{t.count}</span>}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="two-col">
            <div>
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-head">
                  <span className="card-title">
                    <span className="material-symbols-outlined">diversity_3</span>Relationship
                  </span>
                </div>
                <div className="card-body">
                  <dl className="kv">
                    <dt>Strength</dt>
                    <dd>
                      <InlineEditCell
                        value={rel?.RelationshipStrength ?? null}
                        type="select"
                        options={options('RELATIONSHIP_STRENGTH').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel }))}
                        placeholder="Set strength"
                        ariaLabel="Relationship strength"
                        onCommit={patch('RelationshipStrength', 'Relationship strength')}
                        render={(v) => <StatusBadge domain="RELATIONSHIP_STRENGTH" value={v} />}
                      />
                    </dd>
                    <dt>Status</dt>
                    <dd>
                      <InlineEditCell
                        value={rel?.ContactStatus ?? null}
                        type="select"
                        options={options('CONTACT_STATUS').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel }))}
                        placeholder="Set status"
                        ariaLabel="Contact status"
                        onCommit={patch('ContactStatus', 'Status')}
                      />
                    </dd>
                    <dt>Next Move</dt>
                    <dd>
                      <InlineEditCell
                        value={rel?.NextMove ?? null}
                        type="longtext"
                        displayClass="next-move"
                        placeholder="Set next move"
                        ariaLabel="Next move"
                        onCommit={patch('NextMove', 'Next move')}
                      />
                    </dd>
                    <dt>Follow-Up</dt>
                    <dd>
                      <InlineEditCell
                        value={rel?.NextFollowUpDate?.slice(0, 10) ?? null}
                        type="date"
                        placeholder="Set date"
                        ariaLabel="Follow-up date"
                        onCommit={patch('NextFollowUpDate', 'Follow-up date')}
                        render={(v) => <DueBadge date={v} waiting={Boolean(rel?.WaitingForResponse)} />}
                      />
                    </dd>
                    <dt>Owner</dt>
                    <dd>
                      <InlineEditCell
                        value={rel?.OwnerUserId ?? null}
                        placeholder="Unassigned"
                        ariaLabel="Owner"
                        onCommit={patch('OwnerUserId', 'Owner')}
                      />
                    </dd>
                    <dt>Last Contact</dt>
                    <dd>{formatDate(rel?.LastInteractionAt)}</dd>
                    <dt>Source</dt>
                    <dd>{rel?.Source ?? '—'}</dd>
                  </dl>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <span className="card-title">
                    <span className="material-symbols-outlined">history</span>Recent Activity
                  </span>
                </div>
                <div className="card-body">
                  <Timeline activities={profile.Activities.slice(0, 6)} />
                </div>
              </div>
            </div>

            <div>
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-head">
                  <span className="card-title">
                    <span className="material-symbols-outlined">event_upcoming</span>Open Follow-Ups
                  </span>
                </div>
                <div className="card-body">
                  {profile.FollowUps.filter((f) => f.FollowUpStatus === 'Pending').length === 0 ? (
                    <p className="person-sub">None open.</p>
                  ) : (
                    profile.FollowUps
                      .filter((f) => f.FollowUpStatus === 'Pending')
                      .map((f) => (
                        <div key={f.FollowUpId} style={{ marginBottom: 10 }}>
                          <DueBadge date={f.FollowUpDate} />
                          <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 800, color: 'var(--tf-brand)', marginTop: 4 }}>
                            {f.Description || f.FollowUpType}
                          </div>
                          <div className="person-sub">{f.FollowUpType} · {f.OwnerUserId ?? 'unassigned'}</div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* ALWAYS RENDERED, even with no organization. Hiding the card when the field is
                  empty is what made "they just changed jobs" impossible to record: there was
                  nowhere to type the new employer. */}
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-head">
                  <span className="card-title">
                    <span className="material-symbols-outlined">business</span>Organization
                  </span>
                </div>
                <div className="card-body">
                  <InlineEditCell
                    value={profile.Organization?.CompanyName ?? null}
                    placeholder="Set organization"
                    ariaLabel="Organization"
                    onCommit={reassign}
                  />
                  <p className="form-hint" style={{ marginTop: 6 }}>
                    A name that is not already an account creates one. Clearing it detaches them.
                  </p>
                  {profile.Organization && (
                    <>
                      <dl className="kv" style={{ marginTop: 10, gridTemplateColumns: '105px minmax(0,1fr)' }}>
                        <dt>Industry</dt><dd>{profile.Organization.Industry ?? '—'}</dd>
                        <dt>Status</dt><dd>{profile.Organization.CustomerStatus ?? '—'}</dd>
                      </dl>
                      <Link className="btn ghost sm" to="/relationships/organizations"
                            style={{ marginTop: 8 }}>
                        <span className="material-symbols-outlined">open_in_new</span>
                        All organizations
                      </Link>
                    </>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <span className="card-title">
                    <span className="material-symbols-outlined">verified</span>Record
                  </span>
                </div>
                <div className="card-body">
                  <dl className="kv" style={{ gridTemplateColumns: '105px minmax(0,1fr)' }}>
                    <dt>Created By</dt><dd>{c.CreatedBy ?? '—'}</dd>
                    <dt>Created</dt><dd>{formatDate(c.CreatedAt)}</dd>
                    <dt>Updated By</dt><dd>{rel?.UpdatedBy ?? '—'}</dd>
                    <dt>Updated</dt><dd>{formatDate(rel?.UpdatedAt)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'activities' && (
          <div className="card">
            <div className="card-head">
              <span className="card-title">
                <span className="material-symbols-outlined">history</span>Activity Timeline
              </span>
              <button className="btn primary sm" type="button" onClick={() => setLogOpen(true)}>
                <span className="material-symbols-outlined">add</span>Log Activity
              </button>
            </div>
            <div className="card-body">
              {profile.Activities.length === 0 ? (
                <EmptyState icon="history" title="No activity yet" message="Log the first interaction." />
              ) : (
                <Timeline activities={profile.Activities} />
              )}
            </div>
          </div>
        )}

        {tab === 'opportunities' && (
          <div className="card">
            <div className="card-head">
              <span className="card-title">
                <span className="material-symbols-outlined">trending_up</span>Opportunities
              </span>
            </div>
            <div className="card-body">
              {profile.Opportunities.length === 0 ? (
                <EmptyState icon="trending_up" title="No opportunities" message="Nothing is attached to this person yet." />
              ) : (
                profile.Opportunities.map((o) => (
                  <div key={o.OpportunityId} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--tf-line)' }}>
                    <div>
                      <div className="person-name">{o.OpportunityName}</div>
                      <div className="person-sub">{o.OpportunityType} · {o.Stage}</div>
                      <div className="next-move" style={{ marginTop: 5 }}>{o.NextAction ?? '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 900, color: 'var(--tf-brand)' }}>
                        {o.EstimatedValue ? `$${Number(o.EstimatedValue).toLocaleString()}` : '—'}
                      </div>
                      <div className="person-sub">{o.Probability ?? 0}%</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {tab === 'talent' && profile.TalentProfile && (
          <div className="card">
            <div className="card-head">
              <span className="card-title"><span className="material-symbols-outlined">badge</span>Talent Profile</span>
            </div>
            <div className="card-body">
              <dl className="kv">
                <dt>Current Role</dt><dd>{profile.TalentProfile.CurrentRole ?? '—'}</dd>
                <dt>Experience</dt><dd>{profile.TalentProfile.YearsExperience ?? '—'} years</dd>
                <dt>Availability</dt><dd>{profile.TalentProfile.Availability ?? '—'}</dd>
                <dt>Work Auth</dt><dd>{profile.TalentProfile.WorkAuthorization ?? '—'}</dd>
                <dt>Candidate Status</dt><dd>{profile.TalentProfile.CandidateStatus ?? '—'}</dd>
              </dl>
              <div className="ph-badges" style={{ marginTop: 12 }}>
                {profile.Skills.map((s: any) => <Tag key={s.TalentSkillId}>{s.SkillName}</Tag>)}
              </div>
            </div>
          </div>
        )}

        {tab === 'files' && (
          <div className="card">
            <div className="card-head">
              <span className="card-title"><span className="material-symbols-outlined">folder</span>Documents</span>
            </div>
            <div className="card-body">
              <DocumentUpload contactId={id} onDone={() => void load()} />
              {profile.Documents.length === 0 ? (
                <EmptyState icon="upload_file" title="No documents" message="Attach a resume to make it searchable across candidates." />
              ) : (
                <table className="dt">
                  <caption className="sr-only">Documents with type, version and search index state.</caption>
                  <thead>
                    <tr><th>Document</th><th>Type</th><th>Version</th><th>Search Index</th></tr>
                  </thead>
                  <tbody>
                    {profile.Documents.map((d) => (
                      <tr key={d.DocumentId}>
                        <td><span className="person-name">{d.FileName}</span></td>
                        <td><Tag>{d.DocumentType}</Tag></td>
                        <td><span className="person-sub">v{d.VersionNumber}</span></td>
                        <td>
                          {/* Uploaded and searchable are different states, reported separately. */}
                          <span className={`badge b-${d.IsSearchIndexed ? 'strong' : d.IndexStatus === 'FAILED' ? 'blocked' : 'developing'}`}>
                            <span className="dot" />
                            {d.ExcludeFromSearch ? 'Excluded by policy' : d.IndexStatus}
                          </span>
                          {d.ChunkCount ? <div className="person-sub">{d.ChunkCount} chunks</div> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === 'relationships' && (
          <div className="card">
            <div className="card-head">
              <span className="card-title"><span className="material-symbols-outlined">hub</span>Connections</span>
            </div>
            <div className="card-body">
              {profile.Links.length === 0 ? (
                <EmptyState icon="hub" title="No recorded connections" />
              ) : (
                profile.Links.map((l: any) => (
                  <div key={l.EntityLinkId} style={{ marginBottom: 9, fontSize: 'var(--fs-sm)' }}>
                    <span className="person-sub">{l.LinkType}</span> — {l.ToType} {l.ToId}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {logOpen && (
        <LogActivityModal contactId={id} name={name} onClose={() => setLogOpen(false)} onDone={() => void load()} />
      )}
      {noteOpen && (
        <AddNoteModal contactId={id} name={name} onClose={() => setNoteOpen(false)} onDone={() => void load()} />
      )}
      {editOpen && (
        <EditPersonModal
          profile={profile}
          onClose={() => setEditOpen(false)}
          onSaved={() => void load()}
        />
      )}
      {removeOpen && (
        <ConfirmDialog
          title={`Remove ${name}?`}
          message="They come off every list and their open follow-ups are cancelled. Their activities and notes are KEPT — that history is the record of what was actually done, and deleting it would leave the account with calls nobody can account for. This can be undone."
          confirmLabel="Remove"
          danger
          onConfirm={() => void remove()}
          onCancel={() => setRemoveOpen(false)}
        />
      )}
    </>
  )
}

function Timeline({ activities }: { activities: PersonProfile['Activities'] }) {
  if (!activities.length) return <p className="person-sub">No activity recorded.</p>
  return (
    <div className="timeline">
      {activities.map((a) => (
        <div className="tl-item" key={a.ActivityId}>
          <div className={`tl-icon ${a.Direction === 'INBOUND' ? 'inbound' : 'outbound'}`}>
            <span className="material-symbols-outlined">
              {a.ActivityType.includes('Email') ? 'mail'
                : a.ActivityType.includes('Call') ? 'call'
                : a.ActivityType.includes('Meeting') ? 'groups' : 'history'}
            </span>
          </div>
          <div className="tl-body">
            <div className="tl-head">
              <span className="tl-type">{a.ActivityType}</span>
              <span className="tl-who">· {a.Direction === 'INBOUND' ? 'inbound' : 'outbound'}</span>
              <span className="tl-time">{formatShortDate(a.OccurredAt)}</span>
            </div>
            {a.Body && <p className="tl-text">{a.Body}</p>}
            {a.Outcome && (
              <div className="tl-outcome"><b>Outcome:</b><span>{a.Outcome}</span></div>
            )}
            {a.NextActionText && (
              <div className="tl-next">
                <span className="material-symbols-outlined">arrow_forward</span>
                {a.NextActionText}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export function LogActivityModal({ contactId, name, onClose, onDone }: {
  contactId: number; name: string; onClose: () => void; onDone: () => void
}) {
  const { options, notify, notifyError } = useApp()
  const [type, setType] = useState('Phone Call')
  const [direction, setDirection] = useState('OUTBOUND')
  const [body, setBody] = useState('')
  const [outcome, setOutcome] = useState('')
  const [waiting, setWaiting] = useState(false)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await tfApi.logActivity({
        SubjectId: contactId, ActivityType: type, Direction: direction,
        Body: body || undefined, Outcome: outcome || undefined,
        CreatesWaitingForResponse: waiting,
      })
      notify({ tone: 'ok', title: 'Activity logged', body: `${type} with ${name}` })
      onDone(); onClose()
    } catch (err) {
      notifyError('Could not log the activity', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title={`Log activity — ${name}`}
      description="One interaction, one line. Recording the outcome is worth more later than recording that you spoke."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Log'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="form-label" htmlFor="la-type">Type</label>
          <select id="la-type" className="form-input" value={type} onChange={(e) => setType(e.target.value)}>
            {options('ACTIVITY_TYPE').map((v) => <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="la-dir">Direction</label>
          {/* Only OUTBOUND moves the outreach clock — that is why this is a question, not a default. */}
          <select id="la-dir" className="form-input" value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="OUTBOUND">Outbound — I contacted them</option>
            <option value="INBOUND">Inbound — they contacted me</option>
            <option value="INTERNAL">Internal note</option>
          </select>
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="la-body">What happened?</label>
          <textarea id="la-body" className="form-input" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="la-outcome">Outcome</label>
          <input id="la-outcome" className="form-input" value={outcome} onChange={(e) => setOutcome(e.target.value)} />
        </div>
        <div className="field span-2">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 'var(--fs-sm)' }}>
            <input type="checkbox" checked={waiting} onChange={(e) => setWaiting(e.target.checked)} />
            They now owe me a reply
          </label>
        </div>
      </div>
    </ModalShell>
  )
}

export function AddNoteModal({ contactId, name, onClose, onDone }: {
  contactId: number; name: string; onClose: () => void; onDone: () => void
}) {
  const { options, notify, notifyError } = useApp()
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [category, setCategory] = useState('General')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await tfApi.createNote({ SubjectId: contactId, Title: title || undefined, BodyText: text, NoteCategory: category })
      notify({ tone: 'ok', title: 'Note saved', body: `Added to ${name}` })
      onDone(); onClose()
    } catch (err) {
      notifyError('Could not save the note', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title={`Add note — ${name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="button" disabled={busy || !text} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="form-label" htmlFor="an-title">Title</label>
          <input id="an-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="an-cat">Category</label>
          <select id="an-cat" className="form-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {options('NOTE_CATEGORY').map((v) => <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>)}
          </select>
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="an-body">Note</label>
          <textarea id="an-body" className="form-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
      </div>
    </ModalShell>
  )
}


/**
 * Attach a document.
 *
 * REPORTS TWO OUTCOMES, NOT ONE. A file can upload perfectly and still be unsearchable — a scanned
 * PDF with no text layer does exactly that. The toast says which happened, because a recruiter who
 * is told only "uploaded" will search for that candidate next week and conclude the search is
 * broken.
 */
function DocumentUpload({ contactId, onDone }: { contactId: number; onDone: () => void }) {
  const { notify, notifyError } = useApp()
  const [documentType, setDocumentType] = useState('RESUME')
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const send = async (file: File) => {
    setBusy(true)
    try {
      const saved = await tfApi.uploadDocument(file, {
        subjectId: contactId,
        documentType,
      })
      if (saved.IsSearchIndexed) {
        notify({
          tone: 'ok',
          title: 'Uploaded and searchable',
          body: `${file.name} — ${saved.ChunkCount ?? 0} passages indexed.`,
        })
      } else if (saved.ExcludeFromSearch) {
        notify({
          tone: 'info',
          title: 'Uploaded — deliberately not indexed',
          body: `${file.name} is stored. This document type is never indexed, so a candidate can never surface because of what it says about them.`,
        })
      } else {
        //  Not an error toast. The upload succeeded; what failed is the indexing, and telling the
        //  recruiter it failed outright invites them to upload the same file again.
        notify({
          tone: 'info',
          title: 'Uploaded, but not searchable yet',
          body: saved.IndexStatusDetail ?? 'The document index did not accept this file.',
        })
      }
      onDone()
    } catch (err) {
      notifyError('The upload failed', err)
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="quick-log" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
      <select
        className="ie-select"
        value={documentType}
        aria-label="Document type"
        onChange={(e) => setDocumentType(e.target.value)}
      >
        <option value="RESUME">Resume</option>
        <option value="CV">CV</option>
        <option value="CERTIFICATION">Certification</option>
        <option value="PROPOSAL">Proposal</option>
        <option value="CONTRACT">Contract</option>
        {/* Never indexed, and the label says so rather than leaving it to be discovered. */}
        <option value="INTERVIEW_NOTE">Interview note (never indexed)</option>
        <option value="CLIENT_FEEDBACK">Client feedback (never indexed)</option>
      </select>
      <input
        ref={inputRef}
        type="file"
        aria-label="Choose a document to attach"
        accept=".pdf,.doc,.docx,.txt,.rtf,.md,.odt,.html,.htm"
        disabled={busy}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void send(file)
        }}
        style={{ fontSize: 'var(--fs-sm)' }}
      />
      {busy && <Spinner />}
    </div>
  )
}
